import { useEffect, useRef, useState } from 'react';
import { Avatar, Button, Modal, Spin, Tag, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFormGroups } from '../../services/addFormApi';
import { getModuleDocuments } from '../../services/documentApi';
import DescriptionBlock from '../detail/DescriptionBlock';
import DocumentViewer from '../DocumentViewer';
import { extractDocuments } from '../detail/renderConfig';

const { Text } = Typography;

function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

function getRecordName(record) {
  if (!record) return '';
  if (record.name) return record.name;
  if (record.fullName) return record.fullName;
  if (record.firstName || record.lastName) return [record.firstName, record.lastName].filter(Boolean).join(' ');
  return record.candidateName ?? record.clientName ?? record.jobTitle ?? '';
}

function getRecordEmail(record) {
  if (!record) return '';
  return record.email ?? record.emailId ?? record.emailAddress ?? '';
}

function getRecordStatus(record) {
  if (!record) return '';
  if (record.status) return record.status;
  if (record.stage) return record.stage;
  if (typeof record.isActive === 'boolean') return record.isActive ? 'Active' : 'Inactive';
  return '';
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return value.label ?? value.name ?? Object.values(value).join(' ');
  return String(value);
}

const DESCRIPTION_KEYS = ['jobDescription', 'description', 'rawDescription', 'jobDescriptionText'];

function isJobModule(moduleName) {
  return String(moduleName ?? '').toLowerCase() === 'job';
}

function isDescriptionField(field) {
  const matchesKey = DESCRIPTION_KEYS.includes(field.field);
  const matchesLabel = /description/i.test(field.label ?? '');
  return matchesKey || matchesLabel;
}

function ProfileCard({ record }) {
  const name = getRecordName(record);
  const email = getRecordEmail(record);
  const status = getRecordStatus(record);

  if (!name && !email) return null;

  return (
    <div className="preview-profile-card">
      <div className="preview-profile-card__info">
        <Avatar className="preview-profile-card__avatar">{getInitials(name)}</Avatar>
        <div className="preview-profile-card__text">
          {name && <span className="preview-profile-card__name">{name}</span>}
          {email && <span className="preview-profile-card__email">{email}</span>}
        </div>
      </div>
      {status && <Tag className="preview-profile-card__status">{status}</Tag>}
    </div>
  );
}

function FieldsDisplay({ fields, moduleName, centralDocuments = [] }) {
  const [previewDocuments, setPreviewDocuments] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);

  if (!fields?.length) return null;

  function isDocumentField(field) {
    const type = String(field?.type ?? '').trim().toLowerCase();
    const identity = `${field?.field ?? ''} ${field?.label ?? ''}`;
    return ['file', 'document', 'upload'].includes(type)
      || /resume|cv|document|attachment/i.test(identity);
  }

  function openDocumentPreview(documents, index) {
    setPreviewDocuments(documents);
    setPreviewIndex(index);
    setDocumentViewerOpen(true);
  }

  return (
    <>
    
    <div className="preview-field-grid">
      {fields.map((field) => {
        const raw = field.value;
        const isArray = Array.isArray(raw) && raw.length > 0;
        const showFormattedDescription = isJobModule(moduleName)
          && isDescriptionField(field)
          && typeof raw === 'string'
          && raw.trim();
        const centralForField = isDocumentField(field)
          ? centralDocuments
            .filter((document) => {
              const documentField = String(
                document?.fieldName
                ?? document?.field
                ?? document?.formField
                ?? document?.key
                ?? '',
              ).trim().toLowerCase();
              const fieldName = String(field?.field ?? '').trim().toLowerCase();
              if (documentField && fieldName && documentField === fieldName) return true;

              // Older resume records may not have fieldName populated. Keep the
              // same resume/CV fallback used by DetailedView's document action.
              const identity = `${field?.field ?? ''} ${field?.label ?? ''}`;
              if (!/resume|cv/i.test(identity)) return false;
              return /resume|cv/i.test(`${documentField} ${document?.fileName ?? ''} ${document?.name ?? ''}`);
            })
            .map((document) => ({
              name: document?.fileName
                || document?.name
                || document?.uploadedFileName
                || document?.originalName
                || field?.label
                || 'Document',
              // IMPORTANT: use the document registry's presigned URL exactly as
              // DetailedView does. Do not rebuild the private S3 object path.
              url: document?.fileUrl
                || document?.downloadUrl
                || document?.signedUrl
                || document?.url
                || '',
              type: document?.mimeType || document?.contentType || document?.type,
            }))
            .filter((document) => document.url)
          : [];

        const documents = isDocumentField(field)
          ? (centralForField.length
            ? centralForField
            : extractDocuments(raw).filter((document) => document.url))
          : [];

        if (documents.length) {
          return (
            <div
              key={field.field ?? field.label}
              className="preview-field-card preview-field-card--documents"
            >
              <span className="preview-field-card__label">{field.label}</span>
              <div className="preview-document-list">
                {documents.map((document, index) => (
                  <button
                    type="button"
                    key={`${document.url}-${index}`}
                    className="preview-document-link"
                    onClick={() => openDocumentPreview(documents, index)}
                    title={`Preview ${document.name || 'document'}`}
                  >
                    <span className="preview-document-link__icon" aria-hidden="true">▣</span>
                    <span className="preview-document-link__name">
                      {document.name || `Document ${index + 1}`}
                    </span>
                    <span className="preview-document-link__action">Preview</span>
                  </button>
                ))}
              </div>
            </div>
          );
        }

        if (isArray) {
          return raw.map((item, idx) => (
            <div key={`${field.field ?? field.label}-${idx}`} className="preview-field-card">
              <span className="preview-field-card__label">
                {field.label}{raw.length > 1 ? ` (${idx + 1})` : ''}
              </span>
              <span className="preview-field-card__value">{formatValue(item)}</span>
            </div>
          ));
        }

        if (showFormattedDescription) {
          return (
            <div key={field.field ?? field.label} className="preview-field-card preview-field-card--description">
              <span className="preview-field-card__label">{field.label}</span>
              <div className="preview-field-card__description">
                <DescriptionBlock raw={raw} />
              </div>
            </div>
          );
        }

        return (
          <div key={field.field ?? field.label} className="preview-field-card">
            <span className="preview-field-card__label">{field.label}</span>
            <span className="preview-field-card__value">{formatValue(raw)}</span>
          </div>
        );
      })}
    </div>
      <DocumentViewer
        documents={previewDocuments}
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        initialIndex={previewIndex}
      />
    </>
  );
}

export default function PreviewPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { moduleName = '', record = null } = location.state ?? {};

  const id = record?.id ?? record?._id;
  const [formGroups, setFormGroups] = useState([]);
  const [loading, setLoading] = useState(Boolean(moduleName && id));
  const [error, setError] = useState(null);
  const [centralDocuments, setCentralDocuments] = useState([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const bodyRef = useRef(null);
  const stepperWrapRef = useRef(null);
  const stepItemRefs = useRef([]);
  const sectionRefs = useRef([]);

  useEffect(() => {
    if (!moduleName || !id) return;

    let cancelled = false;

    Promise.all([
      getFormGroups({ module: moduleName, id }),
      // Match DetailedViewV1: documents are resolved from the central documents
      // registry because its fileUrl is presigned for private S3 objects.
      getModuleDocuments(moduleName, id).catch(() => ({ data: [] })),
    ])
      .then(([data, documentsResponse]) => {
        if (!cancelled) {
          setFormGroups(Array.isArray(data) ? data : []);
          setCentralDocuments(
            Array.isArray(documentsResponse)
              ? documentsResponse
              : (documentsResponse?.data ?? []),
          );
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load preview');
          setCentralDocuments([]);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [moduleName, id]);

  function handleClose() { navigate(-1); }

  function getGroupLabel(group, index) {
    return group?.label ?? group?.name ?? `Section ${index + 1}`;
  }

  function scrollToSection(index) {
    const body = bodyRef.current;
    const section = sectionRefs.current[index];
    if (!body || !section) return;

    const bodyRect = body.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const stepper = body.querySelector('.preview-stepper-wrap');
    const stickyOffset = (stepper?.offsetHeight ?? 0) + 12;
    const targetTop = body.scrollTop + sectionRect.top - bodyRect.top - stickyOffset;

    setActiveSectionIndex(index);
    body.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
  }

  function handleBodyScroll() {
    const body = bodyRef.current;
    if (!body || !sectionRefs.current.length) return;

    const bodyTop = body.getBoundingClientRect().top;
    const stepper = body.querySelector('.preview-stepper-wrap');
    const threshold = bodyTop + (stepper?.offsetHeight ?? 0) + 24;
    let nextActive = 0;

    sectionRefs.current.forEach((section, index) => {
      if (section && section.getBoundingClientRect().top <= threshold) {
        nextActive = index;
      }
    });

    setActiveSectionIndex((current) => (current === nextActive ? current : nextActive));
  }


  // Keep the active section step visible without exposing a horizontal scrollbar.
  // This also runs when the active section changes from normal vertical scrolling.
  useEffect(() => {
    const wrap = stepperWrapRef.current;
    const item = stepItemRefs.current[activeSectionIndex];
    if (!wrap || !item) return;

    const targetLeft = item.offsetLeft - ((wrap.clientWidth - item.offsetWidth) / 2);
    const maxLeft = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
    wrap.scrollTo({
      left: Math.min(Math.max(0, targetLeft), maxLeft),
      behavior: 'smooth',
    });
  }, [activeSectionIndex, formGroups.length]);

  function renderStepper() {
    if (!formGroups.length) return null;

    return (
      <div ref={stepperWrapRef} className="preview-stepper-wrap" aria-label="Preview sections">
        <div className="preview-stepper">
          {formGroups.map((group, index) => (
            <div className="preview-stepper__segment" key={group.name ?? group.id ?? index}>
              <button
                type="button"
                className="preview-stepper__item"
                ref={(node) => { stepItemRefs.current[index] = node; }}
                onClick={() => scrollToSection(index)}
                aria-current={activeSectionIndex === index ? 'step' : undefined}
                title={`Go to ${getGroupLabel(group, index)}`}
              >
                <span
                  className={[
                    'preview-stepper__circle',
                    index < activeSectionIndex ? 'preview-stepper__circle--done' : '',
                    index === activeSectionIndex ? 'preview-stepper__circle--active' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {index + 1}
                </span>
                <span
                  className={[
                    'preview-stepper__label',
                    index === activeSectionIndex ? 'preview-stepper__label--active' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {getGroupLabel(group, index)}
                </span>
              </button>
              {index < formGroups.length - 1 && (
                <span
                  className={[
                    'preview-stepper__line',
                    index < activeSectionIndex ? 'preview-stepper__line--done' : '',
                  ].filter(Boolean).join(' ')}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const modalTitle = (
    <div className="preview-modal-header">
      <span className="preview-modal-module">{moduleName.toUpperCase()}</span>
      <span className="preview-modal-title">{moduleName ? ' Details' : 'Preview'}</span>
    </div>
  );

  const footer = (
    <div className="wizard-footer preview-scroll-footer">
      <div className="wizard-footer-nav">
        <Button onClick={handleClose}>Close</Button>
      </div>
    </div>
  );

  function renderBody() {
    if (!moduleName || !id) {
      return <div className="job-preview-content job-preview-status">Preview id is missing.</div>;
    }
    if (loading) {
      return <div className="job-preview-content job-preview-status"><Spin size="small" /></div>;
    }
    if (error) {
      return <div className="job-preview-content job-preview-status job-preview-error">{error}</div>;
    }
    if (!formGroups.length) {
      return <div className="job-preview-content job-preview-status">No form groups found.</div>;
    }

    return (
      <div className="preview-section-list">
        {formGroups.map((group, index) => (
          <section
            key={group.name ?? group.id ?? index}
            ref={(node) => { sectionRefs.current[index] = node; }}
            className="preview-section-card"
            data-section-index={index}
          >
            <div className="preview-section-card__header">
              <span className="preview-section-card__number">{index + 1}</span>
              <span className="preview-section-card__title">
                {getGroupLabel(group, index)}
              </span>
            </div>
            <div className="preview-section-card__body">
              {group.fields?.length > 0 ? (
                <FieldsDisplay
                  fields={group.fields}
                  moduleName={moduleName}
                  centralDocuments={centralDocuments}
                />
              ) : (
                <Text type="secondary">No fields in this group.</Text>
              )}
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <Modal
      centered
      className="wizard-modal preview-modal preview-scroll-modal"
      footer={footer}
      open
      title={modalTitle}
      width="min(900px, calc(100vw - 32px))"
      onCancel={handleClose}
      maskClosable={false}
    >
      <div
        ref={bodyRef}
        className="wizard-body preview-scroll-body"
        onScroll={handleBodyScroll}
      >
        {!loading && !error && formGroups.length > 0 && renderStepper()}
        {record && <ProfileCard record={record} />}
        {renderBody()}
      </div>
    </Modal>
  );
}
